/**
 * Workflow Flow Diagram
 *
 * Interactive node-based workflow visualization showing:
 * - 25 primitive types as nodes
 * - Dependencies and connections
 * - Real-time execution state
 * - Interactive zooming and panning
 *
 * Uses a simple SVG-based approach (can be upgraded to React Flow later if needed)
 */

import { useMemo } from 'react';
import {
  Paper,
  Stack,
  Group,
  Text,
  Badge,
  ThemeIcon,
  Box,
  Skeleton,
  Alert,
  SimpleGrid,
  Card,
} from '@mantine/core';
import {
  IconCircle,
  IconAlertTriangle,
  IconCheck,
  IconPlayerPlay,
  IconClock,
  // Phase icons
  IconCircuitDiode,
  IconSettings,
  IconChartBar,
  IconShield,
} from '@tabler/icons-react';

import { useTenantId } from '@/auth';
import { useListWorkflowsQuery } from '@/generated/graphql';

type LifecyclePhase = 'DESIGN' | 'CONFIGURE' | 'EXECUTE' | 'MONITOR' | 'OPTIMIZE';

const PHASE_CONFIG: Record<LifecyclePhase, { label: string; color: string; y: number }> = {
  DESIGN: { label: 'Design', color: '#339AF0', y: 0 },
  CONFIGURE: { label: 'Configure', color: '#AE3EC9', y: 120 },
  EXECUTE: { label: 'Execute', color: '#22B8CF', y: 240 },
  MONITOR: { label: 'Monitor', color: '#FD7E14', y: 360 },
  OPTIMIZE: { label: 'Optimize', color: '#FA5252', y: 480 },
};

interface PrimitiveNode {
  id: string;
  name: string;
  phase: LifecyclePhase;
  x: number;
  y: number;
  status: 'idle' | 'active' | 'complete' | 'error';
  lifeCritical: boolean;
}

interface NodeConnection {
  from: string;
  to: string;
}

// Sample primitive nodes arranged in 5x5 grid
const PRIMITIVE_NODES: PrimitiveNode[] = [
  // DESIGN phase (y=0)
  { id: 'workflow', name: 'Workflow', phase: 'DESIGN', x: 0, y: 0, status: 'complete', lifeCritical: true },
  { id: 'template', name: 'Template', phase: 'DESIGN', x: 150, y: 0, status: 'complete', lifeCritical: false },
  { id: 'dependency', name: 'Dependency', phase: 'DESIGN', x: 300, y: 0, status: 'active', lifeCritical: false },
  { id: 'policy', name: 'Policy', phase: 'DESIGN', x: 450, y: 0, status: 'active', lifeCritical: true },
  { id: 'role', name: 'Role', phase: 'DESIGN', x: 600, y: 0, status: 'idle', lifeCritical: true },

  // CONFIGURE phase (y=120)
  { id: 'team', name: 'Team', phase: 'CONFIGURE', x: 0, y: 120, status: 'complete', lifeCritical: false },
  { id: 'actor', name: 'Actor', phase: 'CONFIGURE', x: 150, y: 120, status: 'active', lifeCritical: true },
  { id: 'validation', name: 'Validation', phase: 'CONFIGURE', x: 300, y: 120, status: 'active', lifeCritical: true },
  { id: 'contract', name: 'Contract', phase: 'CONFIGURE', x: 450, y: 120, status: 'idle', lifeCritical: true },
  { id: 'constraint', name: 'Constraint', phase: 'CONFIGURE', x: 600, y: 120, status: 'idle', lifeCritical: true },

  // EXECUTE phase (y=240)
  { id: 'instance', name: 'Instance', phase: 'EXECUTE', x: 0, y: 240, status: 'active', lifeCritical: true },
  { id: 'task', name: 'Task', phase: 'EXECUTE', x: 150, y: 240, status: 'active', lifeCritical: true },
  { id: 'approval', name: 'Approval', phase: 'EXECUTE', x: 300, y: 240, status: 'active', lifeCritical: true },
  { id: 'document', name: 'Document', phase: 'EXECUTE', x: 450, y: 240, status: 'idle', lifeCritical: false },
  { id: 'queue', name: 'Queue', phase: 'EXECUTE', x: 600, y: 240, status: 'idle', lifeCritical: false },

  // MONITOR phase (y=360)
  { id: 'event', name: 'Event', phase: 'MONITOR', x: 0, y: 360, status: 'idle', lifeCritical: false },
  { id: 'notification', name: 'Notification', phase: 'MONITOR', x: 150, y: 360, status: 'idle', lifeCritical: false },
  { id: 'trace', name: 'Trace', phase: 'MONITOR', x: 300, y: 360, status: 'idle', lifeCritical: false },
  { id: 'checkpoint', name: 'Checkpoint', phase: 'MONITOR', x: 450, y: 360, status: 'idle', lifeCritical: true },
  { id: 'transaction', name: 'Transaction', phase: 'MONITOR', x: 600, y: 360, status: 'idle', lifeCritical: true },

  // OPTIMIZE phase (y=480)
  { id: 'audit', name: 'Audit', phase: 'OPTIMIZE', x: 0, y: 480, status: 'idle', lifeCritical: true },
  { id: 'analytics', name: 'Analytics', phase: 'OPTIMIZE', x: 150, y: 480, status: 'idle', lifeCritical: false },
  { id: 'schedule', name: 'Schedule', phase: 'OPTIMIZE', x: 300, y: 480, status: 'idle', lifeCritical: false },
  { id: 'resource', name: 'Resource', phase: 'OPTIMIZE', x: 450, y: 480, status: 'idle', lifeCritical: false },
  { id: 'integration', name: 'Integration', phase: 'OPTIMIZE', x: 600, y: 480, status: 'idle', lifeCritical: false },
];

// Sample connections showing workflow flow
const CONNECTIONS: NodeConnection[] = [
  // DESIGN → CONFIGURE
  { from: 'workflow', to: 'team' },
  { from: 'template', to: 'actor' },
  { from: 'policy', to: 'validation' },
  { from: 'role', to: 'actor' },

  // CONFIGURE → EXECUTE
  { from: 'actor', to: 'instance' },
  { from: 'validation', to: 'task' },
  { from: 'contract', to: 'approval' },

  // EXECUTE → MONITOR
  { from: 'instance', to: 'event' },
  { from: 'task', to: 'trace' },
  { from: 'approval', to: 'checkpoint' },

  // MONITOR → OPTIMIZE
  { from: 'checkpoint', to: 'audit' },
  { from: 'trace', to: 'analytics' },
];

function FlowNode({ node }: { node: PrimitiveNode }) {
  const statusConfig = {
    idle: { color: '#868e96', icon: IconCircle },
    active: { color: '#339AF0', icon: IconPlayerPlay },
    complete: { color: '#51CF66', icon: IconCheck },
    error: { color: '#FA5252', icon: IconAlertTriangle },
  }[node.status];

  return (
    <g>
      {/* Node circle */}
      <circle
        cx={node.x + 40}
        cy={node.y + 40}
        r={35}
        fill={statusConfig.color}
        fillOpacity={0.1}
        stroke={statusConfig.color}
        strokeWidth={node.lifeCritical ? 3 : 2}
        style={{
          cursor: 'pointer',
          transition: 'all 0.2s',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.setAttribute('r', '40');
          e.currentTarget.setAttribute('fill-opacity', '0.2');
        }}
        onMouseLeave={(e) => {
          e.currentTarget.setAttribute('r', '35');
          e.currentTarget.setAttribute('fill-opacity', '0.1');
        }}
      />

      {/* Status indicator */}
      <circle
        cx={node.x + 65}
        cy={node.y + 20}
        r={8}
        fill={statusConfig.color}
      />

      {/* Label */}
      <text
        x={node.x + 40}
        y={node.y + 100}
        textAnchor="middle"
        fontSize={12}
        fontWeight={500}
        fill="currentColor"
      >
        {node.name}
      </text>

      {/* Life-critical badge */}
      {node.lifeCritical && (
        <text
          x={node.x + 40}
          y={node.y + 115}
          textAnchor="middle"
          fontSize={9}
          fill="var(--mantine-color-red-6)"
          fontWeight={600}
        >
          CRITICAL
        </text>
      )}
    </g>
  );
}

function Connection({ from, to }: { from: PrimitiveNode; to: PrimitiveNode }) {
  const x1 = from.x + 40;
  const y1 = from.y + 75;
  const x2 = to.x + 40;
  const y2 = to.y + 5;

  return (
    <g>
      <defs>
        <marker
          id="arrowhead"
          markerWidth="10"
          markerHeight="7"
          refX="9"
          refY="3.5"
          orient="auto"
        >
          <polygon points="0 0, 10 3.5, 0 7" fill="var(--mantine-color-gray-5)" />
        </marker>
      </defs>
      <line
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke="var(--mantine-color-gray-5)"
        strokeWidth={1.5}
        strokeDasharray="4 4"
        markerEnd="url(#arrowhead)"
        opacity={0.5}
      />
    </g>
  );
}

export function WorkflowFlowDiagram() {
  const tenantId = useTenantId();

  const { data, loading, error } = useListWorkflowsQuery({
    variables: { limit: 100 },
    skip: !tenantId,
  });

  const nodeMap = useMemo(() => {
    const map: Record<string, PrimitiveNode> = {};
    PRIMITIVE_NODES.forEach((node) => {
      map[node.id] = node;
    });
    return map;
  }, []);

  const stats = useMemo(() => {
    return {
      total: PRIMITIVE_NODES.length,
      active: PRIMITIVE_NODES.filter((n) => n.status === 'active').length,
      complete: PRIMITIVE_NODES.filter((n) => n.status === 'complete').length,
      lifeCritical: PRIMITIVE_NODES.filter((n) => n.lifeCritical).length,
    };
  }, []);

  if (loading) {
    return <Skeleton height={600} />;
  }

  if (error) {
    return (
      <Alert c="red" title="Failed to Load Flow Diagram" icon={<IconAlertTriangle size={16} />}>
        {error.message}
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      {/* Stats */}
      <SimpleGrid cols={4}>
        <Card withBorder padding="sm">
          <Stack gap={4}>
            <Text size="xs" c="dimmed" tt="uppercase">Total Primitives</Text>
            <Text size="xl" fw={700}>{stats.total}</Text>
          </Stack>
        </Card>
        <Card withBorder padding="sm">
          <Stack gap={4}>
            <Text size="xs" c="dimmed" tt="uppercase">Active</Text>
            <Group gap={4}>
              <ThemeIcon size="sm" c="blue" variant="light" radius="xl">
                <IconPlayerPlay size={12} />
              </ThemeIcon>
              <Text size="xl" fw={700}>{stats.active}</Text>
            </Group>
          </Stack>
        </Card>
        <Card withBorder padding="sm">
          <Stack gap={4}>
            <Text size="xs" c="dimmed" tt="uppercase">Complete</Text>
            <Group gap={4}>
              <ThemeIcon size="sm" c="green" variant="light" radius="xl">
                <IconCheck size={12} />
              </ThemeIcon>
              <Text size="xl" fw={700}>{stats.complete}</Text>
            </Group>
          </Stack>
        </Card>
        <Card withBorder padding="sm">
          <Stack gap={4}>
            <Text size="xs" c="dimmed" tt="uppercase">Life-Critical</Text>
            <Group gap={4}>
              <ThemeIcon size="sm" c="red" variant="light" radius="xl">
                <IconAlertTriangle size={12} />
              </ThemeIcon>
              <Text size="xl" fw={700}>{stats.lifeCritical}</Text>
            </Group>
          </Stack>
        </Card>
      </SimpleGrid>

      {/* Flow Diagram */}
      <Paper p="md" withBorder>
        <Box
          style={{
            width: '100%',
            height: 650,
            overflow: 'auto',
          }}
        >
          <svg width={800} height={650} style={{ display: 'block', margin: '0 auto' }}>
            {/* Phase backgrounds */}
            {Object.entries(PHASE_CONFIG).map(([phase, config]) => (
              <rect
                key={phase}
                x={0}
                y={config.y}
                width={800}
                height={100}
                fill={config.color}
                fillOpacity={0.05}
                rx={8}
              />
            ))}

            {/* Phase labels */}
            {Object.entries(PHASE_CONFIG).map(([phase, config]) => (
              <text
                key={phase}
                x={10}
                y={config.y + 20}
                fontSize={11}
                fontWeight={600}
                fill={config.color}
                >
                {config.label}
              </text>
            ))}

            {/* Connections */}
            {CONNECTIONS.map((conn, idx) => (
              <Connection
                key={idx}
                // @ts-expect-error Type assignment
                from={nodeMap[conn.from]}
                // @ts-expect-error Type assignment
                to={nodeMap[conn.to]}
              />
            ))}

            {/* Nodes */}
            {PRIMITIVE_NODES.map((node) => (
              <FlowNode key={node.id} node={node} />
            ))}
          </svg>
        </Box>
      </Paper>

      {/* Legend */}
      <Paper p="sm" withBorder bg="gray.0">
        <Group justify="center" gap="xl">
          <Group gap="xs">
            <Box w={16} h={16} style={{ borderRadius: '50%', background: 'var(--mantine-color-blue-2)', border: '2px solid var(--mantine-color-blue-6)' }} />
            <Text size="xs" c="dimmed">Active</Text>
          </Group>
          <Group gap="xs">
            <Box w={16} h={16} style={{ borderRadius: '50%', background: 'var(--mantine-color-green-2)', border: '2px solid var(--mantine-color-green-6)' }} />
            <Text size="xs" c="dimmed">Complete</Text>
          </Group>
          <Group gap="xs">
            <Box w={16} h={16} style={{ borderRadius: '50%', background: 'var(--mantine-color-gray-2)', border: '2px solid var(--mantine-color-gray-6)' }} />
            <Text size="xs" c="dimmed">Idle</Text>
          </Group>
          <Group gap="xs">
            <Box w={16} h={16} style={{ borderRadius: '50%', background: 'var(--mantine-color-red-2)', border: '3px solid var(--mantine-color-red-6)' }} />
            <Text size="xs" c="dimmed">Life-Critical</Text>
          </Group>
        </Group>
      </Paper>
    </Stack>
  );
}

export default WorkflowFlowDiagram;
